import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { MessageFeedback } from '@mem-ai-hub/shared';
import { setFeedback } from '@/services/feedbackApi';

interface FeedbackControlsProps {
  messageId: string;
  sessionId: string;
  initial?: MessageFeedback;
}

const THUMB_UP_PATH =
  'M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z';
const THUMB_DOWN_PATH =
  'M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398C20.613 14.547 19.833 15 19 15h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384';

type Rating = 'up' | 'down' | null;

interface MenuPosition {
  top: number;
  left: number;
}

const POPOVER_WIDTH = 288; // w-72
const POPOVER_HEIGHT = 168; // approximate, for above/below flip

export default function FeedbackControls({
  messageId,
  sessionId,
  initial,
}: FeedbackControlsProps) {
  const [rating, setRating] = useState<Rating>(initial?.rating ?? null);
  const [pending, setPending] = useState(false);
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0 });

  const downButtonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  /** Persist a rating change, optimistically updating and reverting on failure. */
  const submit = useCallback(
    async (next: Rating, nextComment?: string) => {
      const previous = rating;
      setPending(true);
      setRating(next);
      try {
        await setFeedback(sessionId, messageId, next, nextComment);
      } catch (err) {
        console.error('Failed to save feedback:', err);
        setRating(previous);
      } finally {
        setPending(false);
      }
    },
    [rating, sessionId, messageId],
  );

  const handleThumbUp = useCallback(() => {
    if (pending) return;
    setOpen(false);
    void submit(rating === 'up' ? null : 'up');
  }, [pending, rating, submit]);

  const handleThumbDown = useCallback(() => {
    if (pending) return;
    if (rating === 'down') {
      // Already disliked — a second click clears it.
      void submit(null);
      return;
    }
    // Open the "tell us more" popover; the rating is recorded on Submit/Skip.
    setComment('');
    setOpen(true);
  }, [pending, rating, submit]);

  /** Position the popover relative to the thumbs-down button. */
  const updatePosition = useCallback(() => {
    if (!downButtonRef.current) return;
    const rect = downButtonRef.current.getBoundingClientRect();
    let top = rect.bottom + 4;
    // Flip above the button when there isn't room below.
    if (top + POPOVER_HEIGHT > window.innerHeight - 4) {
      top = Math.max(4, rect.top - POPOVER_HEIGHT - 4);
    }
    let left = rect.left;
    if (left + POPOVER_WIDTH > window.innerWidth - 4) {
      left = Math.max(4, window.innerWidth - POPOVER_WIDTH - 4);
    }
    setPosition({ top, left });
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        downButtonRef.current && !downButtonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Reposition on scroll (capture, to catch the scrollable chat container) and resize.
  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  const handleSubmitComment = () => {
    setOpen(false);
    void submit('down', comment.trim() || undefined);
  };

  const handleSkip = () => {
    setOpen(false);
    void submit('down');
  };

  const upActive = rating === 'up';
  const downActive = rating === 'down';

  const popover = open ? (
    <div
      ref={popoverRef}
      className="w-72 rounded-md border border-border bg-bg p-3 shadow-lg"
      style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 50 }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { setOpen(false); downButtonRef.current?.focus(); }
      }}
    >
      <label htmlFor={`feedback-comment-${messageId}`} className="mb-1.5 block text-xs font-medium text-text-secondary">
        Help us improve — what went wrong? (optional)
      </label>
      <textarea
        id={`feedback-comment-${messageId}`}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={1000}
        autoFocus
        placeholder="Tell us more…"
        className="block w-full resize-none rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm
          text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <div className="mt-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleSkip}
          className="rounded-md px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-bg-tertiary"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleSubmitComment}
          className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary-dark"
        >
          Submit
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={handleThumbUp}
        disabled={pending}
        aria-pressed={upActive}
        className={`inline-flex items-center justify-center rounded-md p-1.5 transition-colors disabled:opacity-50
          ${upActive ? 'text-success' : 'text-text-muted hover:bg-bg-tertiary hover:text-text-secondary'}`}
        title="Good response"
        aria-label="Good response"
      >
        <svg
          className="h-4 w-4"
          fill={upActive ? 'currentColor' : 'none'}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.7}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={THUMB_UP_PATH} />
        </svg>
      </button>

      <button
        ref={downButtonRef}
        type="button"
        onClick={handleThumbDown}
        disabled={pending}
        aria-pressed={downActive}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`inline-flex items-center justify-center rounded-md p-1.5 transition-colors disabled:opacity-50
          ${downActive ? 'text-error' : 'text-text-muted hover:bg-bg-tertiary hover:text-text-secondary'}`}
        title="Bad response"
        aria-label="Bad response"
      >
        <svg
          className="h-4 w-4"
          fill={downActive ? 'currentColor' : 'none'}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.7}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={THUMB_DOWN_PATH} />
        </svg>
      </button>

      {popover && createPortal(popover, document.body)}
    </>
  );
}
